<script setup lang="ts">
import { useDisplay } from 'vuetify';
import { ref, computed } from 'vue';
import { useLocale } from '@/plugins/translation';
import { DrawerBottom } from '@ilteducation/components';
import { useElementSize } from '@vueuse/core';
import FallingLeaves from '@/components/FallingLeaves.vue';

const { smAndDown } = useDisplay();
const { t, l } = useLocale();

const filterHeaderRef = ref<any>(null);
const stickyHeaderRef = ref<HTMLElement | null>(null);
const { height: stickyHeaderHeight } = useElementSize(stickyHeaderRef);

const filterTop = computed(() => {
  // 64px (top menu) + 8px (0.5rem gap) + sticky header height
  return 72 + stickyHeaderHeight.value;
});

defineProps<{
  isFiltersVisible: boolean;
}>();
const emit = defineEmits(['removeAllFilters', 'toggleFilter']);

const removeAllFilters = () => {
  emit('removeAllFilters');
};

const toggleFilter = () => emit('toggleFilter');
</script>

<template>
  <div class="top-container pt-2">
    <div class="content-wrapper">
      <div ref="stickyHeaderRef" class="sticky-header bg-surface-container pt-3">
        <!-- The header stays opaque to hide the results scrolling under it, so it gets its own copy of the leaves -->
        <div class="sticky-header-leaves position-absolute">
          <FallingLeaves />
        </div>
        <div class="text-center">
          <slot name="title"></slot>
        </div>
        <slot name="top-panel"></slot>
      </div>
      <div class="search-content" :class="{ 'flex-column-reverse': smAndDown }">
        <div v-show="isFiltersVisible" class="search-filter order-2 position-relative pa-0">
          <DrawerBottom
            v-if="smAndDown"
            :model-value="isFiltersVisible"
            :top-bar-ref="filterHeaderRef?.$el || null"
            @close="toggleFilter"
          >
            <template #topbar>
              <v-card-title ref="filterHeaderRef" class="d-flex justify-space-between align-center px-0">
                <v-btn
                  class="rounded-lg text-none pa-1"
                  size="large"
                  color="primary"
                  variant="text"
                  :text="t(l.button_reset)"
                  @click="removeAllFilters"
                />
                <v-spacer />
                <p class="text-center flex-1-0">{{ t(l.content_search.label_search_filters) }}</p>
                <v-spacer />
                <v-btn
                  class="rounded-lg text-none pa-1"
                  size="large"
                  color="primary"
                  variant="text"
                  @click="toggleFilter"
                >
                  {{ t(l.button_done) }}
                </v-btn>
              </v-card-title>
            </template>
            <slot name="filters" :filter-top="filterTop"></slot>
          </DrawerBottom>
          <v-scroll-x-transition v-else>
            <slot name="filters" :filter-top="filterTop"></slot>
          </v-scroll-x-transition>
        </div>
        <div class="content-area px-6 py-4 bg-surface-bright rounded-xl order-1">
          <slot name="content"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'vuetify/settings';
@use '@/foundations/theme/styles' as s;

:deep(thead) {
  display: none;
}
:deep(.v-img__error) {
  position: unset;
}

.content-wrapper {
  max-width: 78.125rem;
  margin: 0 auto;
  padding: 0 1rem;
}

.sticky-header {
  display: flex;
  flex-direction: column;
  position: sticky;
  top: calc(#{s.$top-menu-and-safe-area} + 0.5rem);
  z-index: 999;
  background-color: rgb(var(--v-theme-surface-container));

  // Pseudo-element fills the 0.5rem gap above to prevent content from peeking through
  &::before {
    content: '';
    position: absolute;
    top: -0.5rem;
    left: -1rem;
    right: -1rem;
    height: 0.5rem;
    background-color: rgb(var(--v-theme-surface-container));
    z-index: -1;
  }

  :deep(.search-header) {
    position: relative;
  }

  // SearchPanel paints its own background, which would hide the leaves behind the search row
  :deep(.search-header),
  :deep(.search-header > .bg-surface-container) {
    background-color: transparent !important;
  }
}

// Same area as the ::before gap filler; clip-path also clips the leaves' position: fixed layer
.sticky-header-leaves {
  inset: -0.5rem -1rem 0;
  z-index: -1;
  clip-path: inset(0);
}

.search-content {
  display: flex;
  gap: 1.5rem;
}

.search-filter {
  flex: 0 0 100%;

  @media (min-width: 960px) {
    flex: 0 0 25%;
    max-width: 25%;
  }
}

.content-area {
  flex: 1 1 0%;
  min-width: 0;
}
</style>
